import React, { useEffect, useState } from 'react';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import MUITableCell from '@material-ui/core/TableCell';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Checkbox from '@material-ui/core/Checkbox';
import useDataSourceContext from '../DataSource/useDataSourceContext';
import styled from 'styled-components';
import Icon from '../Icon';
import IconButton from '@material-ui/core/IconButton';

const TableHeader = styled(TableHead)`
  && {
    border-top: 1px solid #e0e0e0;
  }
`;

const TableCell = styled(MUITableCell)`
  && {
    white-space: nowrap;
  }
`;

const FilterIcon = styled(Icon)`
  && {
    font-size: 1rem;
  }
`;

const FilterIconButton = styled(IconButton)`
  && {
    margin-right: ${props => props.theme.spacing(2)}px;
    margin-left: ${props => props.theme.spacing(0.5)}px;
  }
`;

const DataGridHeader = () => {
  const {
    data,
    hasModule,
    selection = {},
    sorting = {},
    dataProperties = {},
    search = {},
    filters = {},
    setShowFilterSelection,
  } = useDataSourceContext();
  const {
    allItemsSelected,
    setDeselectedItems,
    setSelectedItems,
    deselectItems,
    selectItems,
    deselectedItems = [],
    selectedItems = [],
  } = selection;
  const { activeSort = [], setActiveSort } = sorting;
  const { getActiveProperties } = dataProperties;
  const { searchValue } = search;
  const {
    getFilter,
    getFixedFilter,
    createFilterSelection,
    loadFilterSelection,
    getActiveFilter,
  } = filters;
  const hasSelectionModule = hasModule('selection');
  const hasSortingModule = hasModule('sorting');
  const hasFiltersModule = hasModule('filters');
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const itemIds = data.map(item => item.id);

  /**
   * Open the filter selection drawer width the filter for the selected column open.
   * @param key
   * @returns {function(): void}
   */
  const handleOpenFilter = key => () => {
    const hasActiveSelection = getActiveFilter(key);

    if (hasActiveSelection) {
      loadFilterSelection(key);
    } else {
      createFilterSelection(key);
    }

    setShowFilterSelection(true);
  };

  /**
   * Re-calculates whether the row selection checkbox should be checked or be indeterminate.
   */
  useEffect(() => {
    const selectedRowsOnPage = selectedItems.filter(itemId => itemIds.includes(itemId));

    let indeterminate =
      selectedRowsOnPage.length > 0 && itemIds.length - selectedRowsOnPage.length > 0;
    let checked = selectedRowsOnPage.length > 0 && selectedRowsOnPage.length === itemIds.length;

    if (allItemsSelected) {
      const deselectedRowsOnPage = deselectedItems.filter(itemId => itemIds.includes(itemId));

      indeterminate =
        deselectedRowsOnPage.length > 0 && itemIds.length - deselectedRowsOnPage.length > 0;
      checked = deselectedRowsOnPage.length === 0;
    }

    setIsIndeterminate(indeterminate);
    setIsChecked(checked);
  }, [itemIds, selectedItems, deselectedItems, allItemsSelected]);

  /**
   * Return the column heading elements.
   * @returns {array}
   */
  const getColumnHeadings = () =>
    getActiveProperties().map(
      ({ key, name, sortKey, align = 'left', width, isSortable = true, padding = 'default' }) => {
        const sortingKey = sortKey || key;
        const cellSort = activeSort.find(sortOption => sortOption.key === sortingKey);
        const hasFilter = getFilter && getFilter(key);
        const isFixedFilter = getFixedFilter && getFixedFilter(key);
        const hasActiveFilter = getActiveFilter && getActiveFilter(key);

        return (
          <TableCell key={key} align={align} style={{ width }} padding={padding}>
            {hasSortingModule && isSortable && !searchValue?.length ? (
              <TableSortLabel
                active={!!cellSort}
                direction={cellSort ? cellSort.direction : undefined}
                onClick={() =>
                  setActiveSort([
                    {
                      key: sortingKey,
                      direction: cellSort && cellSort.direction === 'asc' ? 'desc' : 'asc',
                    },
                  ])
                }
              >
                {name}
              </TableSortLabel>
            ) : (
              name
            )}
            {hasFiltersModule && hasFilter && !isFixedFilter && (
              <FilterIconButton
                size="small"
                onClick={handleOpenFilter(key)}
                color={hasActiveFilter ? 'primary' : undefined}
              >
                <FilterIcon icon={['fal', 'filter']} size="small" />
              </FilterIconButton>
            )}
          </TableCell>
        );
      },
    );

  /**
   * Handle the change of the row selection checkbox.
   * @param {boolean} selectAll
   */
  const handleSelect = selectAll => () => {
    if (selectAll) {
      selectItems(itemIds);
      setDeselectedItems(deselectedItems.filter(itemId => !itemIds.includes(itemId)));
    } else {
      setSelectedItems(selectedItems.filter(itemId => !itemIds.includes(itemId)));
      deselectItems(itemIds);
    }
  };

  return (
    <TableHeader>
      <TableRow>
        {hasSelectionModule && (
          <TableCell key="checkbox" padding="checkbox">
            <Checkbox
              checked={isChecked}
              indeterminate={isIndeterminate}
              onChange={handleSelect(!isChecked || isIndeterminate)}
            />
          </TableCell>
        )}
        {getColumnHeadings()}
      </TableRow>
    </TableHeader>
  );
};

export default DataGridHeader;
